import React from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import {
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	List,
	ListItem,
	Tooltip,
	Typography
} from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import { Icon, SquareChip, TagsArray, Text, Utils } from 'app/main/components';
import { useSettings } from 'app/main/components/hooks';

interface CompaniesDuplicateAlertDialogProps {
	duplicateCompanies: any[];
	onSave: VoidFunction;
	onCancel: VoidFunction;
}

function CompaniesDuplicateItem({ model }: { model: any }) {
	const { t } = useSettings();

	const openInNew = () => Utils.openLink({ pathname: `/companies/details/${model.comp_uid}`, newTab: true });

	return (
		<ListItem
			className="last:border-b-0"
			divider
			disableGutters
			secondaryAction={
				<IconButton onClick={openInNew}>
					<Icon icon="open-in-new" />
				</IconButton>
			}
		>
			<Grid container direction="column">
				<Grid className="inline-flex items-center">
					{/* WARNING */}
					{model.comp_warning_desc && (
						<Tooltip title={model.comp_warning_desc} arrow>
							<Icon className="mr-2" icon="alert-circle-outline" color="warning.main" />
						</Tooltip>
					)}
					{/* BLOCKED */}
					{model.comp_blocked_desc && (
						<Tooltip title={model.comp_blocked_desc} arrow>
							<Icon className="mr-2" icon="block" color="error.main" />
						</Tooltip>
					)}
					{/* LEAD */}
					{model.lead_comp_title && (
						<Tooltip title={model.lead_comp_title} arrow>
							<Icon className="mr-2" pack="ic" icon="round-person-pin" />
						</Tooltip>
					)}
					{/* TITLE */}
					<Text variant="subtitle2" to={{ module: 'companies', uid: model.acti_uid }}>
						{model.comp_title}
					</Text>
				</Grid>
				<Grid>
					{/* ADDRESS */}
					<Text className="font-400" variant="subtitle2" color="text.secondary">
						{model.comp_address || ''}
					</Text>
				</Grid>
				<Grid>
					{/* ADDRESS 2 */}
					<Text className="font-400" variant="subtitle2" color="text.secondary">
						{Utils.formatAddress2({
							t,
							address: {
								zip: model.comp_zip,
								city: model.comp_city,
								district: model.dist_iso,
								country: model.ctry_title
							}
						})}
					</Text>
				</Grid>
				<Grid>
					{/* RELATION */}
					{model.core_title && <SquareChip label={model.core_title} backgroundColor={model.core_color} />}
					{/* TYPE */}
					{model.coty_title && <SquareChip label={model.coty_title} backgroundColor={model.coty_color} />}
					{/* SUBTYPES */}
					{model.comp_companies_subtypes?.map(subtype => (
						<SquareChip
							key={subtype.cosa_id}
							label={subtype.cosy_title}
							backgroundColor={subtype.cosy_color}
						/>
					))}
					{/* TAGS */}
					<TagsArray tags={model.comp_tags} />
				</Grid>
			</Grid>
		</ListItem>
	);
}

export const openCompaniesDuplicateAlertDialog = (props: CompaniesDuplicateAlertDialogProps) =>
	openDialog({
		children: <CompaniesDuplicateAlertDialog {...props} />,
		maxWidth: 'sm',
		fullWidth: true
	});

export default function CompaniesDuplicateAlertDialog(props: CompaniesDuplicateAlertDialogProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const handleSave = () => {
		props.onSave();
		dispatch(closeDialog());
	};

	const handleClose = () => {
		props.onCancel();
		dispatch(closeDialog());
	};

	return (
		<>
			<DialogTitle>
				<FormattedMessage
					id="companiesDuplicateAlertDialog.title"
					defaultMessage={t('COMPANIES_DUPLICATE_ALERT_DIALOG_TITLE')}
					values={{ count: props.duplicateCompanies.length }}
				/>
			</DialogTitle>
			<DialogContent>
				<Grid container direction="column" spacing={2}>
					<Grid>
						<Typography>{t('COMPANIES_DUPLICATE_ALERT_DIALOG_DESCRIPTION')}</Typography>
					</Grid>
					<Grid>
						<List disablePadding>
							{props.duplicateCompanies.map((duplicate, index) => (
								<CompaniesDuplicateItem key={index} model={duplicate} />
							))}
						</List>
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button onClick={handleSave}>{t('DIALOG_ACTION_SAVE')}</Button>
			</DialogActions>
		</>
	);
}
